import { useState, useMemo, useEffect, useReducer, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCatalogText } from '@/lib/localizeCatalog';
import { useGetMenuQuery, filterMenuByPeriod, filterMenuByMode, filterMenuByServiceMode } from '@/app/api/resourceApi';
import { modeToPriceType } from './resolveModePrice';
import { currentPeriod } from '@/features/menu/servicePeriod';
import { menuFilterPeriod } from '@/features/menu/menuFilterPeriod';
import { firstSlotFor } from '@/features/checkout/checkoutSlots';
import { restaurantTz } from '@/lib/hours';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { useFlyToCart } from '@/components/fx';
import { useRestaurant } from '@/features/restaurant/useRestaurant';
import { useEffectiveOrderingState } from '@/features/restaurant/useEffectiveOrderingState';
import { emptyWeeklyHours } from '@/types/restaurant';
import { selectIsInDineIn, selectTableSession } from '@/features/tableSession/tableSessionSlice';
import { useSharedTableCart } from '@/features/tableSession/useSharedTableCart';
import { useBuffetRoundGate } from '@/features/tableSession/useBuffetRoundGate';
import { useTableOrderingLock } from '@/features/tableSession/useTableOrderingLock';
import { useAddSharedCartLineMutation } from '@/features/tableSession/sharedCartApi';
import type { SharedAddLineArgs } from '@/features/menu/quickAddToCart';
import { buffetAddBlockMessage } from './buffetAddGate';
import { showErrorToast } from '@/features/ui/uiSlice';
import { selectCartCount, selectCartMode, selectCartSubtotal, selectLinesByRestaurant, openDrawer, removeLine, updateQty } from '@/features/cart/cartSlice';
import { isWaiterTableSession } from '@/features/waiter/waiterSurface';
import GuestLoyaltyTeaser from '@/features/loyalty/components/GuestLoyaltyTeaser';
import CategorySidebar from './CategorySidebar';
import CategoryPills from './CategoryPills';
import DishCard from './DishCard';
import WaiterDishRow from './WaiterDishRow';
import WaiterCartBar from './WaiterCartBar';
import ProductModal from './ProductModal';
import SectionTitle from './SectionTitle';
import PromoBand from './PromoBand';
import OrderModeChooser from './OrderModeChooser';
import { useScrollSpy } from './useScrollSpy';
import { MENU_SCROLL_MARGIN, scrollToMenuSection } from './scrollToMenuSection';
import { quickAddToCart } from './quickAddToCart';
import { sortMenuItems } from './sortMenuItems';
import { categoryColor } from './categoryColor';
import type { MenuItem, OptionGroup } from '@/types';
import type { RestaurantIdentity } from '@/types';
import type { ModalOrigin } from '@/components/ui/Modal';

function usePeriodTick() {
  const [, force] = useReducer((x: number) => x + 1, 0);
  useEffect(() => {
    const id = setInterval(force, 60_000);
    return () => clearInterval(id);
  }, []);
}

const STUB_IDENTITY = {
  uuid: '', name: '', logoUrl: null, faviconUrl: null, address: null, phone: null, country: 'FR',
  hours: emptyWeeklyHours(),
  closures: [],
  reservationClosures: [],
  theme: { primary: '', primaryDeep: '', primaryTeal: '', accent: '', footerGradientFrom: '', footerGradientTo: '' },
  onlineOrderingEnabled: false, onlineOrderingFeatureEnabled: false,
} satisfies RestaurantIdentity;

export default function MenuPage() {
  usePeriodTick();
  const { t } = useTranslation();
  const ct = useCatalogText();
  const { restaurantUuid } = useParams<{ restaurantUuid: string }>();
  const isInDineIn = useAppSelector(selectIsInDineIn);
  const tableSession = useAppSelector(selectTableSession);
  // Fix 6b: gate buffet behaviour on isInDineIn && session.mode==='buffet'
  const isBuffetDineIn = !!(isInDineIn && tableSession?.mode === 'buffet');
  // SC-730 bis : le repérage par couleur est un outil de salle. Il sert au
  // serveur qui connaît son catalogue par cœur et cherche une famille de
  // produits d'un coup d'œil ; le client, lui, découvre la carte et lit des
  // noms — une carte bariolée le gêne plus qu'elle ne l'aide, et la couleur
  // n'est pas un choix de mise en page fait pour lui. Donc : la couleur au
  // carnet du serveur, jamais à la commande en ligne ni au QR scanné à table.
  // Comme SC-692, le drapeau est la table SERVIE, pas le jeton présent sur
  // l'appareil (il survit dans localStorage et le client qui scanne le même
  // téléphone en héritait).
  // Ce meme drapeau commande maintenant tout ce qui separe le carnet du serveur
  // du parcours client sur la carte : la couleur des categories, le saut de
  // categorie instantane (le defilement doux met ~2 s pour traverser 30
  // categories) et la liste en lignes denses au lieu des fiches illustrees.
  const isWaiterNotebook = isWaiterTableSession(tableSession, restaurantUuid ?? '');

  const menuQueryArg = restaurantUuid ?? '';

  const { data: menu, isLoading } = useGetMenuQuery(menuQueryArg, { skip: !restaurantUuid });
  const { identity, config } = useRestaurant(restaurantUuid ?? '');
  const [openItem, setOpenItem] = useState<MenuItem | null>(null);
  const [origin, setOrigin] = useState<ModalOrigin | null>(null);

  const dispatch = useAppDispatch();
  const flyToCart = useFlyToCart();

  // SC-169: in shared-cart mode, adds go to the server-side table cart. The
  // closure bakes in guestToken + nickname + error handling; quickAddToCart and
  // ProductModal pass only item-level fields. undefined ⇒ classic local-cart add.
  const shared = useSharedTableCart();
  const [addSharedCartLine] = useAddSharedCartLineMutation();
  const addSharedLine = useCallback(
    (a: SharedAddLineArgs) => {
      addSharedCartLine({ guestToken: shared.guestToken, nickname: shared.nickname, ...a })
        .unwrap()
        .catch(() => dispatch(showErrorToast(t('tableShared.addError'))));
    },
    [addSharedCartLine, shared.guestToken, shared.nickname, dispatch, t],
  );

  // SC-221/222/223: block adds at cart time when a buffet round restriction
  // applies (max rounds reached, cooldown active, or per-round budget full),
  // instead of only failing at "send to kitchen". The current round's item count
  // is the shared cart in shared mode, else the local cart. Advisory — server
  // stays authoritative; reuses the existing budgetExceeded advisory channel.
  const buffetGate = useBuffetRoundGate();
  const localCartCount = useAppSelector(selectCartCount(restaurantUuid ?? ''));
  const cartSubtotal = useAppSelector(selectCartSubtotal(restaurantUuid ?? ''));
  const roundItemCount = shared.isShared ? shared.count : localCartCount;
  // Spec 2026-08-28 paid-table-ordering-lock — la table a ete reglee au QR.
  // Passe par le meme canal consultatif que le buffet (bouton grise + toast au
  // clic) plutot que d'en ouvrir un second ; le serveur reste seul juge et
  // repond 409 SESSION_PAID_LOCKED a qui poste quand meme. Les deux ne se
  // rencontrent jamais : le verrou ne vise que le mode `ordering`.
  const orderingLocked = useTableOrderingLock();
  const addBlockMessage = orderingLocked
    ? t('tablePayment.lockedAddBlocked')
    : buffetAddBlockMessage({
        isBuffetDineIn,
        roundItemCount,
        roundItemBudget: buffetGate.roundItemBudget,
        roundCount: buffetGate.roundCount,
        maxRounds: buffetGate.maxRounds,
        cooldownSecondsRemaining: buffetGate.cooldownSecondsRemaining,
        t,
      });
  const budgetExceeded = addBlockMessage !== null;

  // Le carnet du serveur affiche la quantite deja au panier sur chaque ligne, et
  // laisse la retirer sans ouvrir le tiroir. `plainLineId` est la ligne sans
  // option ni commentaire : la seule qu'un « − » puisse decrementer sans avoir
  // a demander laquelle des variantes du plat on enleve.
  const cartLines = useAppSelector(selectLinesByRestaurant(restaurantUuid ?? ''));
  const cartByItem = useMemo(() => {
    const byItem = new Map<string, { qty: number; plainLineId: string | null }>();
    for (const line of cartLines) {
      const entry = byItem.get(line.itemId) ?? { qty: 0, plainLineId: null };
      entry.qty += line.qty;
      if (line.selectedOptions.length === 0 && !line.note) entry.plainLineId = line.lineId;
      byItem.set(line.itemId, entry);
    }
    return byItem;
  }, [cartLines]);

  const onDishClick = (item: MenuItem, sourceEl: HTMLElement) => {
    // DF-0161 — la fiche d'un plat dont le groupe obligatoire a ete desactive
    // n'a plus rien a proposer : elle s'ouvrirait vide et le bouton d'ajout y
    // vendrait le plat sans choix. La carte le marque « indisponible » ; cette
    // porte couvre les autres chemins d'appel.
    if (item.unavailableReason) return;
    const r = sourceEl.getBoundingClientRect();
    setOrigin({ x: r.left, y: r.top, width: r.width, height: r.height });
    setOpenItem(item);
  };

  const onQuickAdd = (item: MenuItem, sourceEl: HTMLElement) => {
    if (!visibleMenu || !restaurantUuid) return;
    if (item.unavailableReason) return; // DF-0161

    // Per-product resolved groups; fall back to the shared list. (SC-93)
    const itemGroups: OptionGroup[] =
      item.optionGroups ??
      item.optionGroupIds
        .map((gid) => visibleMenu.optionGroups.find((g) => g.id === gid))
        .filter((g): g is OptionGroup => g !== undefined);
    // Any choice group — required or optional — needs the product modal. A
    // direct quick-add would otherwise silently skip optional extras the guest
    // may want to configure. Only products with zero groups are safe to add.
    if (itemGroups.length > 0) {
      onDishClick(item, sourceEl);
      return;
    }
    quickAddToCart({
      dispatch,
      flyToCart,
      restaurantUuid,
      item,
      itemGroups,
      sourceEl,
      flyImageFallback: identity?.logoUrl ?? undefined,
      budgetExceeded,
      budgetExceededMessage: addBlockMessage ?? undefined,
      addSharedLine: shared.isShared ? addSharedLine : undefined,
      isBuffetDineIn,
      mode: isInDineIn ? 'dine_in' : cartMode,
    });
  };

  const cartMode = useAppSelector(selectCartMode(restaurantUuid ?? ''));
  // DF-0330 — the current/remaining service is the RESTAURANT's, not the
  // browser's timezone.
  const tz = restaurantTz(identity ?? {});
  // Dine-in eats NOW → filter by the current period. Pre-order filters by the
  // periods still orderable today, so between services (currentPeriod 'closed')
  // a midi-only dish is hidden once only dinner slots remain (SC-495).
  const period = isInDineIn
    ? currentPeriod(new Date(), identity?.hours, tz)
    : menuFilterPeriod(
        identity?.hours,
        new Date(),
        identity?.closures ?? [],
        (cartMode === 'delivery' ? config?.prepTimeLivraisonMin : config?.prepTimeEmporterMin) ?? 0,
        tz,
        firstSlotFor(identity, cartMode),
      );
  const orderMode = modeToPriceType(isInDineIn ? 'dine_in' : cartMode);
  const visibleMenu = useMemo(
    () =>
      menu
        ? filterMenuByServiceMode(
            filterMenuByMode(filterMenuByPeriod(menu, period), orderMode),
            isBuffetDineIn ? 'buffet' : 'classic',
          )
        : menu,
    [menu, period, orderMode, isBuffetDineIn],
  );
  // SC-1280 — le restaurant « utilise » les allergènes dès qu'un plat de la carte chargée en déclare.
  const allergensDeclared = useMemo(
    () => !!menu?.items.some((it) => it.allergens !== undefined),
    [menu],
  );
  const sortedCats = useMemo(() => [...(visibleMenu?.categories ?? [])].sort((a, b) => a.order - b.order), [visibleMenu]);
  // SC-730: les plats par categorie sont calcules une fois — la barre de
  // categories en a besoin pour afficher le compte et pour ne pas proposer une
  // categorie dont aucune section n'est rendue (la toucher ne faisait rien).
  const itemsByCat = useMemo(() => {
    const byCat = new Map<string, MenuItem[]>();
    for (const cat of sortedCats) {
      byCat.set(
        cat.id,
        sortMenuItems(
          (visibleMenu?.items ?? []).filter((i) => i.categoryId === cat.id),
          cat.useCustomProductOrder ?? false,
        ),
      );
    }
    return byCat;
  }, [sortedCats, visibleMenu]);
  const catCounts = useMemo(
    () => Object.fromEntries([...itemsByCat].map(([id, items]) => [id, items.length])),
    [itemsByCat],
  );
  // Une categorie sans plat visible ne rend aucune section : la proposer dans
  // la barre ou la colonne menait a un saut vers rien.
  const pickableCats = useMemo(
    () => sortedCats.filter((c) => (itemsByCat.get(c.id)?.length ?? 0) > 0),
    [sortedCats, itemsByCat],
  );
  const ids = useMemo(() => pickableCats.map((c) => `cat-${c.id}`), [pickableCats]);
  const activeSectionId = useScrollSpy(ids);
  const activeCatId = activeSectionId?.replace('cat-', '') ?? pickableCats[0]?.id ?? null;
  const orderingState = useEffectiveOrderingState(identity ?? STUB_IDENTITY, isInDineIn, config?.modes);

  // Les lignes du carnet sont memoisees : une fonction recreee a chaque rendu
  // les re-rendrait toutes a chaque ajout. On passe par une reference vers la
  // derniere version des deux fonctions plutot que par une liste de
  // dependances a tenir a jour.
  const dishClickRef = useRef(onDishClick);
  dishClickRef.current = onDishClick;
  const quickAddRef = useRef(onQuickAdd);
  quickAddRef.current = onQuickAdd;
  const openDish = useCallback((item: MenuItem, el: HTMLElement) => dishClickRef.current(item, el), []);
  const addDish = useCallback((item: MenuItem, el: HTMLElement) => quickAddRef.current(item, el), []);
  const removeDish = useCallback((item: MenuItem) => {
    if (!restaurantUuid) return;
    // Une ligne a 1 disparait, sinon on retire une unite — meme regle que le
    // « − » du tiroir, mais sans avoir a l'ouvrir. On ne touche qu'a la ligne
    // sans option ni commentaire : retirer « laquelle » des variantes d'un plat
    // se decide dans le panier.
    const entry = cartByItem.get(item.id);
    if (!entry?.plainLineId) return;
    const line = cartLines.find((l) => l.lineId === entry.plainLineId);
    if (!line) return;
    if (line.qty <= 1) dispatch(removeLine({ restaurantUuid, lineId: line.lineId }));
    else dispatch(updateQty({ restaurantUuid, lineId: line.lineId, qty: line.qty - 1 }));
  }, [cartByItem, cartLines, restaurantUuid, dispatch]);

  const onPick = (catId: string) => {
    const el = document.getElementById(`cat-${catId}`);
    if (el) {
      // 'instant', pas 'auto' : `html { scroll-behavior: smooth }` est pose dans
      // site.css, et 'auto' veut precisement dire « prends la valeur CSS ».
      // SC-838 : le saut passe par un filet de recalage — la WebView de WeChat
      // laisse la page sur la mauvaise categorie sans lui.
      scrollToMenuSection(el, isWaiterNotebook ? 'instant' : 'smooth');
    }
  };

  if (isLoading || !menu || !visibleMenu || !restaurantUuid || !identity || !config) return null;

  return (
    <>
      {/* Waiter mode shows the table number in the unified WaiterBar, so the
          banner drops its "Table N" line there — it only survives to carry the
          shared-cart notice. Guest QR keeps the full banner. */}
      {isInDineIn && tableSession && (!tableSession.waiterMode || shared.isShared) && (
        <div className="bg-mint/20 border-b border-mint px-4 py-1.5 font-mono text-sm text-ink/80">
          {!tableSession.waiterMode && t('dineInBanner.tableLabel', { n: tableSession.tableNumber })}
          {shared.isShared && (
            <span className="block text-ink/70 mt-0.5">{t('tableShared.banner')}</span>
          )}
        </div>
      )}
      {/* Dire pourquoi les « + » ne repondent plus, la ou le convive regarde.
          Un bouton grise sans phrase se lit comme une panne. */}
      {orderingLocked && (
        <div
          data-testid="table-ordering-locked-banner"
          role="status"
          className="bg-amber-50 border-b-2 border-ink px-4 py-2 font-mono text-sm text-ink"
        >
          {t('tablePayment.lockedBanner')}
        </div>
      )}
      {/* SC-1092 : en ligne, le mode (et donc le prix) se choisit avant le
          premier plat. Sur place il n'y a qu'un prix ; carte fermee, rien a
          commander. */}
      {!isInDineIn && orderingState.canOrder && <OrderModeChooser restaurantUuid={restaurantUuid} />}
      <CategoryPills
        categories={pickableCats}
        activeId={activeCatId}
        onPick={onPick}
        counts={catCounts}
        colored={isWaiterNotebook}
        frenchOnly={isWaiterNotebook}
      />
      <div
        className={`max-w-[1440px] mx-auto px-2 sm:px-4 lg:px-6 pt-2 lg:pt-4 ${
          isWaiterNotebook && localCartCount > 0 ? 'pb-20' : 'pb-6'
        }`}
      >
        <div className="lg:grid lg:grid-cols-[200px_1fr] lg:gap-6 lg:mt-0">
          <CategorySidebar
            categories={pickableCats}
            activeId={activeCatId}
            onPick={onPick}
            colored={isWaiterNotebook}
            frenchOnly={isWaiterNotebook}
          />
          <div className="min-w-0 lg:pt-2">
            {/* SC-1640 : un invite ne voyait la fidelite qu'une fois au panier.
                Une ligne, en tete de la carte, la ou il choisit ; rien a table
                (pas de fidelite sur une commande a table) ni carte fermee. */}
            {!isInDineIn && orderingState.canOrder && (
              <GuestLoyaltyTeaser restaurantUuid={restaurantUuid} />
            )}
            {config.promo && orderingState.canOrder ? <PromoBand promo={config.promo} /> : null}
            <div className={isWaiterNotebook ? 'space-y-2' : 'space-y-4 md:space-y-5'}>
              {pickableCats.map((cat) => {
                const items = itemsByCat.get(cat.id) ?? [];
                return (
                  <section
                    key={cat.id}
                    id={`cat-${cat.id}`}
                    /* SC-838 : la marge de saut suit la hauteur MESUREE des
                       barres collantes (en-tete + barre des categories) — une
                       constante ne peut pas convenir a la fois au parcours sur
                       place, au parcours a emporter et a l'ordinateur. Les
                       classes restent dessous : un moteur sans variables CSS
                       jette le calc et retrouve l'ancien comportement. */
                    className="scroll-mt-[230px] lg:scroll-mt-[100px]"
                    style={{ scrollMarginTop: MENU_SCROLL_MARGIN }}
                  >
                    <SectionTitle
                      emoji={cat.emoji}
                      title={isWaiterNotebook ? cat.name : ct.name(cat)}
                      count={items.length}
                      /* La description de categorie est ecrite pour le client
                         qui decouvre la carte ; sur le carnet du serveur elle
                         mange une ligne par section. */
                      subtitle={isWaiterNotebook ? undefined : ct.description(cat) || undefined}
                      color={isWaiterNotebook ? categoryColor(cat) : undefined}
                      compact={isWaiterNotebook}
                    />
                    {isWaiterNotebook ? (
                      /* gap-2 et pas moins : deux « + » separes de 4 px, c'est
                         le plat du dessous ajoute par erreur en plein service. */
                      <ul className="grid list-none grid-cols-1 gap-2 p-0 m-0 md:grid-cols-2 xl:grid-cols-3">
                        {items.map((item) => {
                          const entry = cartByItem.get(item.id);
                          const hasOptions =
                            (item.optionGroups?.length ?? item.optionGroupIds.length) > 0;
                          return (
                            <WaiterDishRow
                              key={item.id}
                              item={item}
                              /* En panier partage le panier local est vide :
                                 le compteur mentirait. */
                              qty={shared.isShared ? 0 : (entry?.qty ?? 0)}
                              hasOptions={hasOptions}
                              canStep={!hasOptions && !shared.isShared && !!entry?.plainLineId}
                              orderingEnabled={orderingState.canOrder}
                              color={categoryColor(cat)}
                              isBuffetDineIn={isBuffetDineIn}
                              activeMode={isInDineIn ? 'dine_in' : cartMode}
                              onOpen={openDish}
                              onAdd={addDish}
                              onRemove={removeDish}
                            />
                          );
                        })}
                      </ul>
                    ) : (
                      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 md:gap-5">
                        {items.map((item, idx) => (
                          <DishCard
                            key={item.id}
                            index={idx}
                            item={item}
                            onClick={(el) => onDishClick(item, el)}
                            onQuickAdd={onQuickAdd}
                            orderingEnabled={orderingState.canOrder}
                            restaurantLogoUrl={identity.logoUrl}
                            restaurantName={identity.name}
                            themeColor={identity.theme.primary}
                            restaurantUuid={restaurantUuid}
                            allergens={item.allergens?.contains}
                          />
                        ))}
                      </div>
                    )}
                  </section>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {isWaiterNotebook && (
        <WaiterCartBar
          count={localCartCount}
          subtotalCents={cartSubtotal}
          onOpen={() => dispatch(openDrawer())}
        />
      )}

      <ProductModal
        open={openItem !== null}
        onClose={() => setOpenItem(null)}
        item={openItem}
        groups={visibleMenu.optionGroups}
        restaurantUuid={restaurantUuid}
        origin={origin}
        orderingEnabled={orderingState.canOrder}
        restaurantLogoUrl={identity.logoUrl}
        restaurantName={identity.name}
        themeColor={identity.theme.primary}
        budgetExceeded={budgetExceeded}
        addSharedLine={shared.isShared ? addSharedLine : undefined}
        isBuffetDineIn={isBuffetDineIn}
        allergens={openItem?.allergens}
        allergensDeclared={allergensDeclared}
      />
    </>
  );
}
